<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <ListPageTemplate
    :id="'audienceLogics_list'"
    :name="$t('names.audienceLogic', { n: 2 })">
    <template #header-icon>
      <IconColoredAudienceLogic />
    </template>
    <template
      v-if="isSidekickOrganization"
      #header-message>
      <feature-message :message="capitalize($t('messages.sidekickTestFeatureDefault'))" />
    </template>
    <template #table-actions>
      <button
        v-show="selectedAudienceLogics.length > 0"
        class="btn btn-danger"
        name="deleteAudienceLogics"
        :disabled="!permissions.canWriteAudiencelogic"
        @click="promptForAudienceLogicsDeletion">
        {{ capitalize($t('actions.delete')) }}
      </button>
      <button
        v-if="organizationSettings.uiEnableAirportAudiences"
        name="newAirportTravellerAudience"
        class="btn btn-primary"
        @click="$router.push('/airport-audiences/new/edit')">
        <icon-air-travel />
        {{ capitalize($t('actions.newAirportTravellerAudience')) }}
      </button>
      <button
        v-if="organizationSettings.uiEnableMovieAudiences"
        name="newMovieAudience"
        class="btn btn-primary"
        :disabled="!permissions.canWriteAudiencemovie"
        @click="$router.push('/movie-audiences/new/edit')">
        <icon-movie />
        {{ capitalize($t('actions.newMovieAudience')) }}
      </button>
      <button
        name="newAudienceLogic"
        class="btn btn-primary"
        :disabled="!permissions.canWriteAudiencelogic"
        @click="$router.push('/audiences/new/edit')">
        {{ capitalize($t('actions.newAudienceLogic')) }}
      </button>
    </template>
    <b-table
      id="audienceLogics_list_table"
      ref="audienceLogicsTable"
      category="AudienceLogics"
      :items="audienceLogics"
      :expand="['audiencerules.audiencelookbackwindows']"
      :columns="columns"
      :show-filters="true"
      :total-count="totalCount"
      :is-loading="isLoading"
      :is-main-table="true"
      :is-editable="true"
      pagination
      enable-views
      allow-selection
      @filters-changed="onTableFiltersChanged"
      @selection-changed="selectedAudienceLogics = $event">
      <template #link="{ item: audienceLogic }">
        <router-link
          :to="audienceLogic.endpoint"
          class="entity-link"
          :title="audienceLogic.name">
          {{ audienceLogic.name }}
        </router-link>
      </template>
      <template #userFriendlyLogicalExpression="{ item: audienceLogic }">
        {{ audienceLogic.userFriendlyLogicalExpression }}
      </template>
      <template #status="{ item: audienceLogic }">
        {{ capitalize($t(`states.${audienceLogic.status}`)) }}
      </template>
      <template #createdBy="{ item: audienceLogic }">
        <UserDetails :user-i-d="audienceLogic.apiObject.created_by" />
      </template>
      <template #no-records>
        <no-records-table
          :icon="markRaw(IconComplexAudienceLogic)"
          :header-text="$t('messages.tablePlaceholderNoAudienceLogicsTitle')"
          :details-text="$t('messages.tablePlaceholderNoAudienceLogicsDescription')"
          :action-text="capitalize($t('actions.addAudienceLogic'))"
          :is-primary="false"
          :action-handler="() => $router.push('/audiences/new/edit')" />
      </template>
      <template #no-match>
        <no-records-table
          :icon="markRaw(IconComplexAudienceLogic)"
          :header-text="$t('messages.tablePlaceholderNoMatchingAudienceLogicsTitle')"
          :details-text="$t('messages.tablePlaceholderNoMatchingItemsDescription')" />
      </template>
    </b-table>

    <modal
      :id="deleteAudienceLogicsConfirmationModalID"
      :visible="isDeleteAudienceLogicsModalVisible"
      :title="deleteAudienceLogicsConfirmationModalTitle"
      :body="deleteAudienceLogicsConfirmationModalBody"
      :button1-caption="yes"
      :button2-caption="no"
      :button2-visible="true"
      @confirmed="confirmAudienceLogicsDeletion"
      @cancelled="cancelAudienceLogicsDeletion">
      <template
        v-if="audiencesLinkedToLineItems.length"
        #modal-after-text>
        <div class="alert alert-danger">
          {{ $t('messages.warnAboutDeletingCustomAudienceWithLineItemData') }}
          <ul>
            <li
              v-for="audience in audiencesLinkedToLineItems"
              :key="audience.id">
              <router-link
                :to="audience.endpoint"
                class="entity-link"
                :title="audience.name"
                target="_blank">
                {{ audience.name }}
              </router-link>
            </li>
          </ul>
        </div>
      </template>
    </modal>
  </ListPageTemplate>
</template>

<script type="text/babel">
  import { CanceledError } from 'axios';
  import { capitalize, markRaw } from 'vue';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import FeatureMessage from '@/common/components/generic-messages/FeatureMessage.vue';
  import ListPageTemplate from '@/common/components/templates/ListPageTemplate.vue';
  import IconColoredAudienceLogic from '@/common/icons/colored/AudienceLogic.vue';
  import { IconComplexAudienceLogic } from '@/common/icons/complex';
  import { IconAirTravel, IconMovie } from '@/common/icons/monochrome';
  import { AudienceLogic, Filter, FilterCollection, QueryParams } from '@/common/models';
  import ErrorHandler from '@/ErrorHandler';
  import UserDetails from '@/org-app/components/users/UserDetails.vue';
  import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
  import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
  import queryClient, { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
  import { UPDATE_AUDIENCELOGIC_NAME } from '@/store/modules/models/mutation-types';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import { waitUntil } from '@/utils/DelayHelper';
  import i18n from '@/vueI18n';

  export default {
    components: {
      'b-table': Table,
      IconAirTravel,
      IconMovie,
      UserDetails,
      ListPageTemplate,
      IconColoredAudienceLogic,
      NoRecordsTable,
      FeatureMessage,
    },
    data() {
      return {
        deleteAudienceLogicsConfirmationModalID: 'delete_audienceLogics_confirmation_modal',
        isDeleteAudienceLogicsModalVisible: false,
        selectedAudienceLogics: [],
        audienceLogics: [],
        totalCount: 0,
        isLoading: true,
        lineItems: [],
        audiencesLinkedToLineItems: [],
        IconComplexAudienceLogic,
      };
    },
    computed: {
      ...mapGetters(['permissions']),
      ...mapGetters('orgApp', ['isDemandOrganization', 'isSupplyOrganization', 'isSidekickOrganization']),
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
      }),
      columns() {
        const columns = {};
        columns.link = TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.name', { n: 1 })),
          property: 'name',
          type: TableColumnTypes.string,
          editablePropertyName: 'name',
          mutation: UPDATE_AUDIENCELOGIC_NAME,
          editableClassName: 'AudienceLogic',
        });
        columns.userFriendlyLogicalExpression = TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.rule', { n: 2 })),
          property: 'logical_expression',
          type: TableColumnTypes.string,
          useAsFilter: false,
          allowSort: false,
        });
        columns.modifiedOnFormatted = TableColumn.fromObject({
          label: capitalize(i18n.global.t('messages.lastModifiedOn')),
          property: 'modified_on_utc',
          type: TableColumnTypes.datetime,
        });
        columns.createdBy = TableColumn.fromObject({
          label: capitalize(i18n.global.t('messages.createdBy')),
          property: 'created_by',
          type: TableColumnTypes.list,
          source: () => runQueryInScope(useUserOrganizations),
          sourceFilterProperty: 'created_by',
          sourceLabel: 'email',
          sourceID: 'userID',
          isDefault: true,
          allowSort: false,
        });
        return columns;
      },
      firstColumn() {
        return this.columns.length > 0 ? this.columns[0] : null;
      },
      no() {
        return capitalize(i18n.global.t('actions.no'));
      },
      yes() {
        return !this.audiencesLinkedToLineItems?.length
          ? capitalize(i18n.global.t('actions.yes'))
          : capitalize(i18n.global.t('actions.retry'));
      },
      deleteAudienceLogicsConfirmationModalTitle() {
        return capitalize(i18n.global.t('actions.deleteAudienceLogics'));
      },
      deleteAudienceLogicsConfirmationModalBody() {
        return capitalize(i18n.global.t('messages.confirmDeleteAudienceLogics'));
      },
    },
    mounted() {
      this.reloadAudienceLogics();
    },
    methods: {
      capitalize,
      markRaw,
      ...mapActions('orgApp', ['createNewAudienceLogic', 'deleteAudienceLogic', 'getAudienceLogics', 'getLineItems']),
      promptForAudienceLogicsDeletion() {
        this.isDeleteAudienceLogicsModalVisible = true;
      },
      async confirmAudienceLogicsDeletion() {
        await this.fetchLineItems();
        const lineItemsDemoIds = this.lineItems?.map((i) => i.demographicIDs).flat();
        this.audiencesLinkedToLineItems = this.selectedAudienceLogics.filter(
          (a) => lineItemsDemoIds.indexOf(a.demographicID) > -1,
        );

        if (!this.audiencesLinkedToLineItems?.length) {
          try {
            const promises = this.selectedAudienceLogics.map((x) => this.deleteAudienceLogic({ instance: x }));
            await Promise.all(promises);
            await queryClient.invalidateQueries({ queryKey: [queryKeys.demographics], refetchType: 'active' });
            await this.reloadAudienceLogics();
          } finally {
            this.isDeleteAudienceLogicsModalVisible = false;
          }
        }
      },
      cancelAudienceLogicsDeletion() {
        this.isDeleteAudienceLogicsModalVisible = false;
        this.audiencesLinkedToLineItems = [];
        this.lineItems = [];
      },
      async fetchAudienceLogics(queryParams) {
        try {
          this.isLoading = true;
          const result = await queryParams.run(this.getAudienceLogics);
          this.totalCount = result.totalCount;
          this.audienceLogics = result.items.map((i) =>
            AudienceLogic.fromObject({
              ...i,
              audienceRules: AudienceSegmentUtils.audienceRulesBeToFe(i),
              logicalExpression: AudienceSegmentUtils.rewriteLogicalExpression(
                i,
                AudienceSegmentUtils.audienceRulesBeToFe(i),
              ),
            }),
          );
          this.isLoading = false;
        } catch (e) {
          if (!(e instanceof CanceledError)) this.isLoading = false;
          ErrorHandler.handleExceptionInUIComponent(e);
        }
      },
      async fetchLineItems() {
        try {
          const demographicIds = this.selectedAudienceLogics.map((a) => a.demographicID);
          const queryParams = QueryParams.for({
            filterCollection: new FilterCollection([
              new Filter('lineitem_demographictargets.demographic_id', demographicIds),
            ]),
          });
          const res = await queryParams.run(this.getLineItems);
          this.lineItems = res.items;
        } catch (e) {
          // Do nothing
        }
      },
      onTableFiltersChanged(queryParams) {
        this.fetchAudienceLogics(queryParams);
      },
      async reloadAudienceLogics() {
        await waitUntil(() => this.$refs.audienceLogicsTable != null);
        const { queryParams } = this.$refs.audienceLogicsTable;
        this.fetchAudienceLogics(queryParams);
      },
    },
  };
</script>
